class Popup {
	static q = [];
	static ov = null;
	static anim = false;
	static escBound = false;
	static stylesInjected = false;

	constructor(size = 'medium', cls = null) {
		this.size = size;
		this.cls = cls;
		this.el = null;
		this.headerEl = null;
		this.titleEl = null;
		this.scrollEl = null;
		this.contentEl = null;
		this.vis = false;
		// Outside click does not close a locked popup (cookie bar: only the X button, Esc or an answer).
		this.lockOutside = false;
	}

	// Is the popup really rendered? Blockers hide cookie bars via display:none or remove them from the DOM.
	static isShown(p) {
		try {
			if(!p || !p.el || !p.el.isConnected) return false;
			const cs = getComputedStyle(p.el);
			if(cs.display === 'none' || cs.visibility === 'hidden') return false;
			const r = p.el.getBoundingClientRect();
			return r.width > 0 && r.height > 0 && r.bottom > 0 && r.right > 0 && r.top < innerHeight && r.left < innerWidth;
		} catch(e) {
			return false;
		}
	}

	static widths() {
		return { small: '400px', medium: '500px', plus: '600px', large: '900px' };
	}

	static injectStyles() {
		if (this.stylesInjected) return;
		this.stylesInjected = true;
		const s = document.createElement('style');
		s.innerHTML = `
			.global-popup-window {
				padding: 0 !important;
				overflow: hidden !important;
				display: flex !important;
				flex-direction: column !important;
			}
			.global-popup-header {
				position: relative;
				flex-shrink: 0;
				background: var(--light, #fff);
				z-index: 10;
				padding: 48px 48px 8px 48px !important;
				display: flex !important;
				justify-content: space-between !important;
				align-items: center !important;
			}
			.global-popup-scroll-wrapper {
				position: relative;
				flex-grow: 1;
				flex-shrink: 1;
				min-height: 0;
				display: flex;
				flex-direction: column;
			}
			.global-popup-gradient-top {
				position: absolute;
				top: 0;
				left: 0;
				width: 100%;
				height: 12px;
				background: linear-gradient(to bottom, var(--light, #ffffff) 0%, rgba(255,255,255,0) 100%);
				pointer-events: none;
				z-index: 10;
			}
			.global-popup-gradient-bottom {
				position: absolute;
				bottom: 0;
				left: 0;
				width: 100%;
				height: 32px;
				background: linear-gradient(to top, var(--light, #ffffff) 0%, var(--light, #ffffff) 8px, rgba(255,255,255,0) 100%);
				pointer-events: none;
				z-index: 10;
			}
			/* Gradients only where there is something to scroll (2026-10-08): top after scrolling down, bottom while more
			   content is below – before they always covered the first and last lines (stars of a review, end of a table). */
			.global-popup-gradient-top, .global-popup-gradient-bottom {
				opacity: 0;
				transition: opacity 0.2s ease;
			}
			.global-popup-scroll-wrapper.gp-top .global-popup-gradient-top,
			.global-popup-scroll-wrapper.gp-more .global-popup-gradient-bottom {
				opacity: 1;
			}
			.global-popup-scroll {
				flex-grow: 1;
				overflow-y: auto;
				padding: 0 48px 33px 48px;
				scrollbar-width: none;
				-ms-overflow-style: none;
			}
			.global-popup-scroll::-webkit-scrollbar {
				display: none;
			}
			.global-popup-close {
				position: relative !important;
				top: auto !important;
				right: auto !important;
				flex-shrink: 0;
				margin-left: auto !important;
				background: none;
				border: none;
				font-size: 28px;
				cursor: pointer;
				color: var(--darkGreen, #666);
				opacity: 0.6;
				transition: opacity 0.2s ease;
				line-height: 1;
				padding: 0;
				width: 28px;
				height: 28px;
				display: flex;
				align-items: center;
				justify-content: center;
			}
			
			@media(max-width: 768px) {
				.global-popup-window {
					width: calc(100vw - 32px) !important;
					max-width: none !important;
				}
				.global-popup-header {
					padding: 24px 20px 4px 20px !important;
				}
				.global-popup-scroll {
					padding: 0 20px 25px 20px !important;
				}
				.global-popup-close {
					margin-left: auto !important;
				}
				.global-popup-gradient-top {
					height: 8px !important;
				}
				.global-popup-gradient-bottom {
					height: 24px !important;
					background: linear-gradient(to top, var(--light, #ffffff) 0%, var(--light, #ffffff) 6px, rgba(255,255,255,0) 100%) !important;
				}
			}
		`;
		document.head.appendChild(s);
	}

	static bindEsc() {
		if(this.escBound) return;
		this.escBound = true;
		document.addEventListener('keydown', e => {
			if(e.key !== 'Escape') return;
			const t = Popup.q[Popup.q.length - 1];
			if(t) t.close();
		});
	}

	static makeOv() {
		if(this.ov) return;
		this.ov = document.createElement('div');
		Object.assign(this.ov.style, {
			position: 'fixed',
			top: '0',
			left: '0',
			width: '100%',
			height: '100%',
			backgroundColor: 'var(--darkOverlay)',
			backdropFilter: 'blur(4px)',
			webkitBackdropFilter: 'blur(4px)',
			opacity: '0',
			transition: 'opacity 0.3s ease',
			zIndex: '9998'
		});
		this.ov.onclick = () => {
			const t = Popup.q[Popup.q.length - 1];
			// A hidden locked popup (blocker) must not trap the page behind the overlay.
			if(t && !(t.lockOutside && Popup.isShown(t))) t.close();
		};
		document.body.appendChild(this.ov);
		this.ov.offsetHeight;
		this.ov.style.opacity = '1';
	}

	static killOv() {
		if(!this.ov) return;
		this.ov.style.opacity = '0';
		setTimeout(() => {
			if(this.ov && this.ov.parentNode) this.ov.parentNode.removeChild(this.ov);
			this.ov = null;
		}, 300);
	}

	create() {
		Popup.injectStyles();
		
		this.el = document.createElement('div');
		this.el.classList.add('global-popup-window');
		
		const w = Popup.widths()[this.size] || Popup.widths().medium;
		Object.assign(this.el.style, {
			position: 'fixed',
			top: '50%',
			left: '50%',
			transform: 'translate(-50%, -50%) scale(0.8)',
			backgroundColor: 'var(--light, #fff)',
			borderRadius: 'var(--borderRadius, 16px)',
			boxShadow: 'var(--shadow, 0 10px 40px rgba(0,0,0,0.2))',
			width: `min(${w}, calc(100vw - var(--pagePadding, 16px) * 2))`,
			maxHeight: 'calc(100vh - var(--pagePadding, 16px) * 2)',
			boxSizing: 'border-box',
			opacity: '0',
			transition: 'opacity 0.3s ease, transform 0.3s ease',
			zIndex: '9999',
			display: 'none'
		});
		
		if(this.cls) this.el.classList.add(...this.cls.split(' '));

		this.headerEl = document.createElement('div');
		this.headerEl.classList.add('global-popup-header');

		this.titleEl = document.createElement('div');
		this.titleEl.style.display = 'none';
		this.headerEl.appendChild(this.titleEl);

		const btn = document.createElement('button');
		btn.innerHTML = '&times;';
		btn.classList.add('global-popup-close');
		btn.onmouseenter = () => btn.style.opacity = '1';
		btn.onmouseleave = () => btn.style.opacity = '0.6';
		btn.onclick = () => this.close();
		
		this.headerEl.appendChild(btn);

		const wrapper = document.createElement('div');
		wrapper.classList.add('global-popup-scroll-wrapper');

		const topGrad = document.createElement('div');
		topGrad.classList.add('global-popup-gradient-top');

		const bottomGrad = document.createElement('div');
		bottomGrad.classList.add('global-popup-gradient-bottom');

		this.scrollEl = document.createElement('div');
		this.scrollEl.classList.add('global-popup-scroll');
		
		this.contentEl = document.createElement('div');
		this.scrollEl.appendChild(this.contentEl);

		wrapper.appendChild(topGrad);
		wrapper.appendChild(bottomGrad);
		wrapper.appendChild(this.scrollEl);

		// Gradient state from the real scroll position; recomputed on scroll, resize and content size changes.
		const scrollEl = this.scrollEl;
		this.updateGradients = () => {
			if(!scrollEl.isConnected) return;
			const max = scrollEl.scrollHeight - scrollEl.clientHeight;
			wrapper.classList.toggle('gp-top', scrollEl.scrollTop > 1);
			wrapper.classList.toggle('gp-more', max > 1 && scrollEl.scrollTop < max - 1);
		};
		scrollEl.addEventListener('scroll', this.updateGradients, {passive: true});
		window.addEventListener('resize', this.updateGradients);
		if(window.ResizeObserver) {
			this.gradientObserver = new ResizeObserver(this.updateGradients);
			this.gradientObserver.observe(this.contentEl);
			this.gradientObserver.observe(scrollEl);
		}

		this.el.appendChild(this.headerEl);
		this.el.appendChild(wrapper);
		
		document.body.appendChild(this.el);
	}

	setTitle(html) {
		try {
			if(!this.el) this.create();
			if(html instanceof HTMLElement) {
				this.titleEl.innerHTML = '';
				this.titleEl.appendChild(html);
			} else {
				this.titleEl.innerHTML = html || '';
			}
			this.titleEl.style.display = html ? 'block' : 'none';
			const f = this.titleEl.firstElementChild;
			if(f) {
				f.style.marginTop = '0';
				f.style.marginBottom = '0';
			}
		} catch(e) {
		}
	}

	setContent(html) {
		try {
			if(!this.el) this.create();
			this.contentEl.innerHTML = '';
			if(html instanceof HTMLElement) this.contentEl.appendChild(html);
			else this.contentEl.innerHTML = html || '';
			if(this.updateGradients) requestAnimationFrame(this.updateGradients);
		} catch(e) {
		}
	}

	show() {
		try {
			if(Popup.anim || this.vis) return;
			Popup.bindEsc();
			if(Popup.q.length === 0) Popup.makeOv();
			const prev = Popup.q[Popup.q.length - 1];
			if(prev && prev !== this) prev.hide();
			Popup.q.push(this);
			if(!this.el) this.create();
			this.el.style.display = 'block';
			this.vis = true;
			this.el.offsetHeight;
			if(this.updateGradients) requestAnimationFrame(this.updateGradients);
			this.el.style.opacity = '1';
			this.el.style.transform = 'translate(-50%, -50%) scale(1)';
			// Brave shields / cookie filter lists hide the bar asynchronously — check repeatedly.
			if(this.lockOutside) [0, 300, 1000, 2500, 5000].forEach(ms => setTimeout(() => this.dropIfBlocked(), ms));
		} catch(e) {
		}
	}

	// Popup hidden by a blocker: drop it silently (no close handler, no choice saved) and remove the overlay.
	dropIfBlocked() {
		try {
			if(!this.vis || Popup.anim || Popup.q[Popup.q.length - 1] !== this || Popup.isShown(this)) return;
			Popup.q.splice(Popup.q.indexOf(this), 1);
			this.destroy();
			const n = Popup.q[Popup.q.length - 1];
			if(n && n.el) {
				n.el.style.display = 'block';
				n.vis = true;
				n.el.style.opacity = '1';
				n.el.style.transform = 'translate(-50%, -50%) scale(1)';
			} else {
				Popup.killOv();
			}
		} catch(e) {
			Popup.killOv();
		}
	}

	hide() {
		try {
			if(!this.el || !this.vis) return;
			this.el.style.display = 'none';
			this.vis = false;
		} catch(e) {
		}
	}

	close() {
		try {
			if(Popup.anim || !this.vis) return;
			Popup.anim = true;
			this.el.style.opacity = '0';
			this.el.style.transform = 'translate(-50%, -50%) scale(0.8)';
			setTimeout(() => {
				try {
					const i = Popup.q.indexOf(this);
					if(i > -1) Popup.q.splice(i, 1);
					this.destroy();
					if(Popup.q.length > 0) {
						const n = Popup.q[Popup.q.length - 1];
						n.el.style.display = 'block';
						n.vis = true;
						n.el.offsetHeight;
						n.el.style.opacity = '1';
						n.el.style.transform = 'translate(-50%, -50%) scale(1)';
					} else {
						Popup.killOv();
					}
				} catch(e) {
					Popup.killOv();
				} finally {
					Popup.anim = false;
				}
			}, 300);
		} catch(e) {
			Popup.anim = false;
			Popup.killOv();
		}
	}

	destroy() {
		if(this.updateGradients) window.removeEventListener('resize', this.updateGradients);
		if(this.gradientObserver) this.gradientObserver.disconnect();
		this.updateGradients = null;
		this.gradientObserver = null;
		if(this.el && this.el.parentNode) this.el.parentNode.removeChild(this.el);
		this.el = null;
		this.headerEl = null;
		this.titleEl = null;
		this.scrollEl = null;
		this.contentEl = null;
		this.vis = false;
	}
}

window.Popup = Popup;
